/*
 * Shared engine for collection infinite scroll.
 *
 * Owns everything that is the same for every view: watching a trigger element,
 * fetching the next page of the section, stopping at the end, restoring the
 * paginated links if it cannot finish, and keeping the product count honest.
 *
 * Callers supply three functions for the parts that genuinely differ:
 *   buildUrl(page)  -> URL for the next page of this section
 *   extract(doc)    -> { nodes, visibleCount } from the parsed response,
 *                      or null when the expected container is missing
 *   append(nodes)   -> insert those nodes into the live DOM; return false if it
 *                      could not, and the page is treated as not loaded
 *
 * Everything else is read from dataSource.dataset:
 *   section, currentPage, totalPages, visibleCount,
 *   countTargets, countSingular, countPlural, loadingText
 *
 * On the count: Shopify paginates before Locksmith hides anything, so
 * collection.products_count over-reports whenever a collection holds locked
 * products. Each page reports what it will really render and this totals them.
 */
class InfiniteScrollController {
  constructor({ trigger, dataSource, buildUrl, extract, append }) {
    this.trigger = trigger;
    this.source = dataSource;
    this.buildUrl = buildUrl;
    this.extract = extract;
    this.append = append;

    const data = dataSource.dataset;
    this.initialPage = parseInt(data.currentPage, 10) || 1;
    this.loadedPage = this.initialPage;
    this.totalPages = parseInt(data.totalPages, 10) || 1;
    this.loadedVisibleCount = parseInt(data.visibleCount, 10) || 0;
    this.isLoading = false;
  }

  start() {
    this.applyCount();
    this.guardCount();

    if (this.loadedPage >= this.totalPages) {
      this.trigger.hidden = true;
      return;
    }

    // Only supersede the paginated links once we know we can take over.
    this.trigger.hidden = false;
    this.paginationWrapper = document.querySelector(
      `#shopify-section-${CSS.escape(this.source.dataset.section)} .pagination-wrapper`
    );
    if (this.paginationWrapper) this.paginationWrapper.hidden = true;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      // Generous bottom margin: the trigger sits above the pagination, any
      // collection description and the footer, so at maximum scroll it can end up
      // above the viewport entirely. Without the slack it would only ever be
      // crossed mid-scroll and loading would stall after the first append.
      { rootMargin: '0px 0px 1200px 0px' }
    );
    this.observer.observe(this.trigger);
  }

  // True once a page beyond the one the section rendered with has been appended.
  // Callers use this to know that the live DOM no longer matches any single-page
  // render of this section.
  get hasAppended() {
    return this.loadedPage > this.initialPage;
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = undefined;
    this.countGuard?.disconnect();
    this.countGuard = undefined;
  }

  async loadNext() {
    if (this.isLoading || this.loadedPage >= this.totalPages) return;
    this.isLoading = true;

    const status = this.trigger.querySelector('[data-infinite-scroll-status]');
    if (status) status.textContent = this.loadingText;

    try {
      const response = await fetch(this.buildUrl(this.loadedPage + 1));
      if (!response.ok) {
        this.stop();
        return;
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = this.extract(doc);

      // A null result means the response had no recognisable container. An empty
      // node list is legitimate — a page can be entirely hidden by Locksmith — so
      // keep going rather than treating it as the end of the collection.
      if (!result) {
        this.stop();
        return;
      }

      // A refused append means the container went away under us. Bail before the
      // bookkeeping: advancing loadedPage past rows that were never inserted would
      // skip that page for good and inflate the count, and the pricing pass would
      // poll detached nodes for fifteen seconds.
      if (this.append(result.nodes) === false) {
        this.stop();
        return;
      }

      this.refreshB2bPricing(result.nodes);
      this.loadedPage += 1;
      this.loadedVisibleCount += result.visibleCount || 0;
      this.applyCount();

      if (this.loadedPage >= this.totalPages) {
        this.stop();
      } else if (this.observer) {
        // IntersectionObserver only reports transitions. On a tall viewport the
        // trigger often stays on screen after appending, so no second callback
        // arrives and loading stalls until the shopper scrolls away and back.
        // Re-observing forces a fresh reading of the current position.
        this.observer.unobserve(this.trigger);
        this.observer.observe(this.trigger);
      }
    } catch (error) {
      console.error(error);
      this.stop();
    } finally {
      this.isLoading = false;
      if (status) status.textContent = '';
    }
  }

  /*
   * BSS B2B applies company pricing client-side, and every step of it is driven
   * by the DOM rather than by anything the theme can hand it directly:
   *
   *   1. it only looks for new products on a window 'scroll' event, matching
   *      [bss-b2b-product-id]:not([bss-b2b-product-active]) and stamping that
   *      marker on everything it finds, then loading the prices;
   *   2. a ProductPriceMutate notification is what actually writes the
   *      discounted price into the element;
   *   3. subject.init() re-attaches its quantity and variant listeners.
   *
   * Appending rows fires none of these — the URL never changes either, so the
   * href poller in bss-custom.js does not cover it. Step 1 is a trap on its own:
   * a bare scroll stamps the marker before the prices arrive, and because every
   * later scan skips marked elements, a row nudged and then left alone keeps its
   * retail price for good. So poll for the marker rather than guessing a delay,
   * and only then notify — repeatedly, to cover the fetch sitting behind it.
   */
  refreshB2bPricing(nodes) {
    if (typeof BSS_B2B === 'undefined' || !BSS_B2B) return;

    const priceElements = nodes.flatMap((node) =>
      Array.from(node.querySelectorAll?.('[bss-b2b-product-id][bss-b2b-product-price]') || [])
    );
    if (!priceElements.length) return;

    const undim = this.dimUntilPriced(priceElements);

    const deadline = Date.now() + 15000;
    let notified = 0;

    const poll = () => {
      // BSS marks every element it matched before it filters them, so products
      // that carry no price rule are marked too and 'every' still settles.
      if (priceElements.every((element) => element.hasAttribute('bss-b2b-product-active'))) {
        const { productSubject, cartSubject } = BSS_B2B.observer || {};
        productSubject?.notifyObserver('ProductMutate', 'ProductPriceMutate');
        // Both are idempotent — they skip anything already carrying the marker.
        productSubject?.init?.();
        cartSubject?.init?.();
        notified += 1;
        // Measured: the rewritten prices land within a second of the first
        // notification, so hold the fade a beat past it and then let go.
        if (notified === 1) setTimeout(undim, 1200);
        if (notified >= 3) return;
      } else {
        window.dispatchEvent(new Event('scroll'));
      }
      if (Date.now() < deadline) setTimeout(poll, 700);
    };

    poll();
  }

  /*
   * Appended rows arrive at the Liquid-rendered retail price and BSS reprices them
   * a few seconds later, so a B2B shopper is briefly shown a figure that is not
   * theirs. Fade the price until it settles.
   *
   * Only worth doing when BSS is really pricing this shopper, and the rows already
   * on the page answer that: inline visibility on a price element is written by the
   * app and by nothing else — section markup arrives with no style attribute at all
   * — so if no settled row carries it, this is a customer BSS does not price and
   * there is nothing to wait for. Retail shoppers, and anyone browsing with the app
   * broken or uninstalled, see no fade.
   *
   * That style is only good as a signal that the app is running, not as a per-row
   * one: a price flow anywhere on the page sweeps it across every price element in
   * the document, so it lands on rows that have not been priced yet. Nor does the
   * app leave anything else behind that separates 'priced' from merely 'detected'.
   * So the caller ends the fade off its own pricing pass instead.
   *
   * Returns the function that ends it, which is safe to call more than once. It also
   * runs on a timeout, so that whatever goes wrong upstream — and however far the
   * app's internals drift from what was measured here — no shopper is ever left
   * looking at a price that stays faded.
   */
  dimUntilPriced(priceElements) {
    const incoming = new Set(priceElements);
    const settled = Array.from(document.querySelectorAll('[bss-b2b-product-price]')).filter(
      (element) => !incoming.has(element)
    );
    if (!settled.some((element) => element.style.visibility)) return () => {};

    priceElements.forEach((element) => {
      element.classList.add('b2b-price-pending');
      element.setAttribute('aria-busy', 'true');
    });

    let released = false;
    const undim = () => {
      if (released) return;
      released = true;
      priceElements.forEach((element) => {
        element.classList.remove('b2b-price-pending');
        element.removeAttribute('aria-busy');
      });
    };

    setTimeout(undim, 8000);
    return undim;
  }

  stop() {
    this.observer?.disconnect();
    this.observer = undefined;
    this.trigger.hidden = true;
    // Hand the links back if we never managed to load everything.
    if (this.paginationWrapper && this.loadedPage < this.totalPages) {
      this.paginationWrapper.hidden = false;
    }
  }

  get loadingText() {
    return this.trigger.dataset.loadingText ?? this.source.dataset.loadingText ?? '';
  }

  get countTargets() {
    return (this.source.dataset.countTargets || '')
      .split(',')
      .map((id) => document.getElementById(id.trim()))
      .filter(Boolean);
  }

  applyCount() {
    const count = this.loadedVisibleCount;
    const data = this.source.dataset;
    const template = count === 1 ? data.countSingular : data.countPlural;
    if (!template) return;

    // The localised strings embed the number once ("{{ count }} products"), so
    // swapping the first run of digits preserves whatever wording the locale uses.
    this.countText = template.replace(/\d+/, count);
    this.writeCount();
  }

  writeCount() {
    if (!this.countText) return;
    this.countTargets.forEach((target) => {
      if (target.textContent.trim() !== this.countText) target.textContent = this.countText;
    });
  }

  guardCount() {
    // facets.js re-renders the count from the fetched markup immediately after
    // replacing the grid, which would put Shopify's inflated figure back. Watch
    // the targets and reassert, rather than depending on the order of those two
    // calls staying as it is today.
    this.countGuard?.disconnect();
    const targets = this.countTargets;
    if (!targets.length) return;

    this.countGuard = new MutationObserver(() => this.writeCount());
    targets.forEach((target) =>
      this.countGuard.observe(target, { childList: true, characterData: true, subtree: true })
    );
  }
}
